import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { PopoverPosition } from '@mui/material';
import { Storey } from '@shared/models/design/storey.model';
import { ActivityType } from '@shared/models/design/activities';

export type AddActivityRefMenuAnchor =
  | {
      anchorEl: HTMLElement;
      anchorPosition?: undefined;
    }
  | {
      anchorEl: null;
      anchorPosition: PopoverPosition;
    };

export type AddActivityRefMenuRequest = AddActivityRefMenuAnchor & {
  storey: Storey;
};

export type AddActivityRefMenuResult = ActivityType | undefined;

type AddActivityRefMenuResolver = (result: AddActivityRefMenuResult) => void;

type AddActivityRefMenuContextValue = {
  request?: AddActivityRefMenuRequest;
  open: (
    request: AddActivityRefMenuRequest,
  ) => Promise<AddActivityRefMenuResult>;
  onSelect: (result: ActivityType) => void;
  onClose: () => void;
};

const AddActivityRefMenuContext =
  createContext<AddActivityRefMenuContextValue | null>(null);

/**
 * Provider som hanterar en singleton-instans av aktivitets-menyn
 * för alla våningsrader. Liknar {@link StoreySettingsProvider}.
 */
export const AddActivityRefMenuProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [request, setRequest] = useState<
    AddActivityRefMenuRequest | undefined
  >();
  const resolverRef = useRef<AddActivityRefMenuResolver | undefined>(undefined);

  /**
   * Stänger aktuell meny och returnerar valt resultat till anroparen.
   */
  const onSelect = useCallback((result: ActivityType) => {
    const resolver = resolverRef.current;
    resolver?.(result);
    resolverRef.current = undefined;
    setRequest(undefined);
  }, []);

  /**
   * Öppnar singleton-menyn med given request och returnerar det
   * {@link Activity} som användaren väljer, eller undefined vid avbrott.
   */
  const open = useCallback(
    (
      nextRequest: AddActivityRefMenuRequest,
    ): Promise<AddActivityRefMenuResult> => {
      return new Promise<AddActivityRefMenuResult>((resolve) => {
        resolverRef.current = resolve;
        setRequest(nextRequest);
      });
    },
    [],
  );

  const onClose = useCallback(() => {
    const resolver = resolverRef.current;
    // Resolve with undefined when menu is closed without selection
    resolver?.(undefined);
    resolverRef.current = undefined;
    setRequest(undefined);
  }, []);

  const value = useMemo(
    () => ({
      request,
      open,
      onSelect,
      onClose,
    }),
    [open, request, onSelect, onClose],
  );

  return (
    <AddActivityRefMenuContext.Provider value={value}>
      {children}
    </AddActivityRefMenuContext.Provider>
  );
};

const useAddActivityRefMenuContext = (): AddActivityRefMenuContextValue => {
  const context = useContext(AddActivityRefMenuContext);
  if (!context) {
    throw new Error('ActivityRefMenuContext is missing a provider.');
  }
  return context;
};

/**
 * Hook för att öppna aktivitets-menyn från valfri komponent.
 */
export const useAddActivityRefMenu = () => {
  const { open } = useAddActivityRefMenuContext();
  return open;
};

/**
 * Hook som används av singleton-komponenten för att läsa aktuell request.
 */
export const useAddActivityRefMenuRequest = () => {
  const { request, onSelect, onClose } = useAddActivityRefMenuContext();
  return { request, onSelect, onClose };
};
